import type { ReactNode } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from 'Context/ReactAppContext';
import { getFromLocalStorage, remove, setInLocalStorage } from 'Client/local-storage-api';
import {
  SHOPPING_FAVORITE_PRODUCTS,
  SHOPPING_FAVORITE_PRODUCTS_NUMBER_MAX,
  SHOPPING_REMOTE_FAVORITE_PRODUCTS,
} from 'Tracking/constants';
import { useAuthentication } from 'Src/hooks/use-authentication/useAuthentication';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import {
  CUSTOMER_ACCOUNT_GET_BOOKMARKS,
  CUSTOMER_ACCOUNT_GET_SEARCHES,
} from 'Utils/request/requestDictionary';
import {
  addBookmarkMutation,
  deleteBookmarkMutation,
  getBookmarksQuery,
  mapBookmarkEntity,
  mapSearchEntity,
} from 'Components/widgets/price-remote/price-remote-favorite/favorite.utils';
import { getUserSearchesQuery } from 'Components/utils/booking/bookingSearch.queries';
import { difference, differenceWith, partition } from 'lodash';
import type { FavoriteProductType } from './FavoriteProductListContext';
import { FavoriteProductListContext } from './FavoriteProductListContext';
type Props = {
  children: ReactNode;
};

const FavoriteProductListProvider = ({ children }: Props) => {
  const { isLogged } = useAuthentication();
  const appContext = useAppContext();
  const localStorageFavoritesName = isLogged
    ? SHOPPING_REMOTE_FAVORITE_PRODUCTS
    : SHOPPING_FAVORITE_PRODUCTS;
  const localFavoriteProductsList = getFromLocalStorage(localStorageFavoritesName) || [];
  const [favoriteProductsState, setFavoriteProductsState] = useState({
    favoriteProductList: localFavoriteProductsList,
    showcaseLatestFavorite: false,
    showAllFavorite: false,
    hasTooManyFavorites: localFavoriteProductsList.length >= SHOPPING_FAVORITE_PRODUCTS_NUMBER_MAX,
  });
  const [loading, setLoading] = useState(true);
  const [travelPlan, setTravelPlan] = useState(null);

  const setHasTooManyFavorites = (hasTooManyFavorites: boolean) =>
    setFavoriteProductsState({ ...favoriteProductsState, hasTooManyFavorites });

  const setFavoriteProductList = (
    favoriteProductList: Array<FavoriteProductType>,
    showcaseLatestFavorite: boolean,
    showAllFavorite: boolean,
  ) => {
    //Update the list of favorites in localStorage or delete it if there are no elements
    favoriteProductList.length
      ? setInLocalStorage(localStorageFavoritesName, favoriteProductList)
      : remove(localStorageFavoritesName);
    setFavoriteProductsState({
      ...favoriteProductsState,
      favoriteProductList,
      showcaseLatestFavorite,
      showAllFavorite,
      hasTooManyFavorites: favoriteProductList.length >= SHOPPING_FAVORITE_PRODUCTS_NUMBER_MAX,
    });
  };

  const addTravelPlan = (proposal: Proposal, product: Product) => {
    setTravelPlan(
      mapSearchEntity({
        adults: proposal.attendees?.adults?.length,
        birthdates: (proposal.attendees?.children || []).map(({ birthdate }) => birthdate),
        returnDate: proposal.departureDate,
        departureDate: proposal.arrivalDate,
        departureCity: proposal.transports?.inbound?.travelSections?.[0]?.departure?.cityId || 'NO',
        price: proposal.price,
        reference: proposal.id,
        resort: product,
      }),
    );
  };

  const syncLocalAndRemoteFavorites = async (remoteBookmarks, localBookmarks, searches) => {
    const bookmarksToSync = differenceWith(
      localBookmarks || [],
      [...remoteBookmarks, ...searches],
      (item1, item2) =>
        item1.adultsNumber === item2.adultsNumber &&
        item1.departureDate === item2.departureDate &&
        item1.duration === item2.duration &&
        item1.departureCity === item2.departureCity &&
        item1.kids.length === item2.kids.length &&
        item1.product.id === item2.product.id,
    );
    const bookmarks = await Promise.all(
      bookmarksToSync.map(async (bookmark) => addBookmarkMutation(bookmark, appContext)),
    );

    if (bookmarksToSync?.length) {
      setInLocalStorage(SHOPPING_FAVORITE_PRODUCTS, difference(localBookmarks, bookmarksToSync));
    }

    //Update the list of favorites with products received from ADD resource or from GET resource
    const favoriteProductList = bookmarks[bookmarks.length - 1]
      ? (bookmarks[bookmarks.length - 1]?.addBookmark || []).map((b) => mapBookmarkEntity(b))
      : remoteBookmarks;
    setFavoriteProductList([...favoriteProductList, ...searches], false, false);
  };

  const value = useMemo(
    () => ({
      favoriteProductsState,
      loading,
      addTravelPlan,
      setHasTooManyFavorites,
      setFavoriteProductList,
    }),
    [favoriteProductsState, loading],
  );
  const { isLoading: bookmarksLoading, data: bookmarksData } = useGQL({
    query: `query { 
      bookmarks {
        ${getBookmarksQuery(appContext)}
      }
    }`,
    queryOptions: {
      requestName: CUSTOMER_ACCOUNT_GET_BOOKMARKS,
    },
    cache: false,
    ignore: !isLogged,
    map: ({ bookmarks }) => bookmarks,
  });
  const { isLoading: searchesLoading, data: searchesData } = useGQL({
    query: getUserSearchesQuery(appContext.locale),
    queryOptions: {
      requestName: CUSTOMER_ACCOUNT_GET_SEARCHES,
    },
    cache: false,
    ignore: !isLogged,
    map: ({ userSearches }) => userSearches,
  });
  useEffect(() => {
    setLoading(bookmarksLoading || searchesLoading);
  }, [bookmarksLoading, searchesLoading]);
  useEffect(() => {
    let bookmarks = [];

    if (!!bookmarksData && !!searchesData) {
      bookmarks = bookmarksData.map((b) => mapBookmarkEntity(b));
      const [bookmarksToDelete, filteredBookmarks] = partition(
        favoriteProductsState.favoriteProductList.filter(({ origin }) => origin === 'BOOKMARK'),
        (item) =>
          item.adultsNumber === travelPlan?.adultsNumber &&
          item.departureDate === travelPlan?.departureDate &&
          item.duration === travelPlan?.duration &&
          item.departureCity === travelPlan?.departureCity &&
          item.kids.length === travelPlan?.kids.length &&
          item.product.id === travelPlan?.product.id,
      );
      bookmarksToDelete.forEach(({ id }) => deleteBookmarkMutation(id, appContext));
      const searches = (searchesData || []).map((b) => mapSearchEntity(b));
      syncLocalAndRemoteFavorites(
        travelPlan ? filteredBookmarks : bookmarks,
        getFromLocalStorage(SHOPPING_FAVORITE_PRODUCTS),
        travelPlan ? [...searches, travelPlan] : searches,
      );
    }
  }, [JSON.stringify(bookmarksData), JSON.stringify(searchesData), travelPlan]);
  return (
    <FavoriteProductListContext.Provider value={value}>
      {children}
    </FavoriteProductListContext.Provider>
  );
};

export default FavoriteProductListProvider;
